/**
 * 处理MinIO图片URL，根据环境返回正确的URL
 * @param {string} imageUrl - 图片路径
 * @returns {string} 完整的图片URL
 */
export function getMinioUrl(imageUrl) {
  if (!imageUrl) return ''

  // 如果已经是完整URL，直接返回
  if (imageUrl.startsWith('http')) {
    return imageUrl
  }

  // 确保imageUrl有正确的格式
  const formattedImageUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`

  // 根据环境使用不同的基础URL
  const baseUrl = process.env.VUE_APP_MINIO_URL

  if (baseUrl) {
    // 开发环境使用完整URL
    return `${baseUrl}${formattedImageUrl}`
  } else {
    // 生产环境使用相对路径或当前域名
    return process.env.NODE_ENV === 'production'
      ? `${window.location.origin}/minio${formattedImageUrl}`
      : formattedImageUrl
  }
}

/**
 * 处理课程封面URL，统一转换为正确的访问路径
 * @param {string} imageUrl - 课程封面路径
 * @returns {string} 处理后的图片URL
 */
export function getCourseCoverUrl(imageUrl) {
  // 检查是否有有效的封面URL
  if (!imageUrl || imageUrl.trim() === '' || imageUrl === 'default') {
    return ''
  }

  // 如果是完整的MinIO URL，直接使用
  if (imageUrl.startsWith('http://localhost:9000/') || imageUrl.startsWith('http://127.0.0.1:9000/')) {
    return imageUrl
  }

  // 如果已经是/api/file开头的URL，直接返回
  if (imageUrl.startsWith('/api/file/')) {
    return imageUrl
  }

  // 处理相对路径，统一转换为course-cover bucket
  let bucketPath = ''
  if (imageUrl.startsWith('/course-cover/')) {
    bucketPath = imageUrl
  } else if (imageUrl.startsWith('course-cover/')) {
    bucketPath = '/' + imageUrl
  } else if (imageUrl.startsWith('/question-info/')) {
    // 如果原来是question-info，转换为course-cover
    const fileName = imageUrl.substring('/question-info/'.length)
    bucketPath = '/course-cover/' + fileName
  } else if (imageUrl.startsWith('question-info/')) {
    // 如果原来是question-info，转换为course-cover
    const fileName = imageUrl.substring('question-info/'.length)
    bucketPath = '/course-cover/' + fileName
  } else {
    // 其他情况，假设是文件名，添加到course-cover bucket
    bucketPath = '/course-cover/' + imageUrl
  }

  // 通过后端文件预览接口访问
  return `/api/file/preview?path=${encodeURIComponent(bucketPath)}`
}

/**
 * 处理用户头像URL，统一转换为正确的访问路径
 * @param {string} avatarUrl - 用户头像路径
 * @returns {string} 处理后的图片URL
 */
export function getUserAvatarUrl(avatarUrl) {
  // 检查是否有有效的头像URL
  if (!avatarUrl || avatarUrl.trim() === '') {
    return ''
  }

  // 如果是完整的MinIO URL，直接使用
  if (avatarUrl.startsWith('http')) {
    return avatarUrl
  }

  // 如果已经是/api/file开头的URL，直接返回
  if (avatarUrl.startsWith('/api/file/')) {
    return avatarUrl
  }

  // 处理相对路径，统一转换为question-info bucket（用户头像存储在question-info桶中）
  let bucketPath = ''
  if (avatarUrl.startsWith('/question-info/')) {
    bucketPath = avatarUrl
  } else if (avatarUrl.startsWith('question-info/')) {
    bucketPath = '/' + avatarUrl
  } else if (avatarUrl.startsWith('/course-cover/')) {
    // 如果原来是course-cover，转换为question-info
    const fileName = avatarUrl.substring('/course-cover/'.length)
    bucketPath = '/question-info/' + fileName
  } else if (avatarUrl.startsWith('course-cover/')) {
    // 如果原来是course-cover，转换为question-info
    const fileName = avatarUrl.substring('course-cover/'.length)
    bucketPath = '/question-info/' + fileName
  } else {
    // 其他情况，假设是文件名，添加到question-info bucket
    bucketPath = '/question-info/' + avatarUrl
  }

  // 若配置了 MinIO 基础地址，则直接拼接，避免经过后端预览接口
  const baseUrl = process.env.VUE_APP_MINIO_URL
  if (baseUrl) {
    return `${baseUrl}${bucketPath}`
  }

  // 回退：通过后端文件预览接口访问
  return `/api/file/preview?path=${encodeURIComponent(bucketPath)}`
}
